/** @module CartItem */

import {
  getProductPackName,
  getProductPackPrice,
  getProductPackUsage,
  getProductPackSize,
  getProductPackReference,
  getProductPackFormulaIds,
  getProductPackUniqueId,
  isProductPackGenericEyeSerum,
  isProductPackSerum
} from '@/models/core/ProductPack';

/**
 * A product pack added to cart.
 *
 * @typedef {Object} CartItem
 * @property {ProductPack} productPack
 * @property {number} quantity
 */

/**
 * Create cartItem.
 *
 * @function
 * @param {ProductPack} productPack
 * @param {number} [quantity]
 * @return {CartItem}
 */
export const CartItem = (productPack, quantity = 0) => ({ productPack, quantity });

/**
 * Increase cart item quantity by one.
 *
 * @function
 * @param {CartItem} cartItem {@link CartItem}
 * @return {CartItem}
 */
export const incrementCartItemQuantity = cartItem => {
  const quantity = cartItem.quantity + 1;

  return CartItem(cartItem.productPack, quantity);
};

/**
 * Decrease cart item quantity by one.
 *
 * @function
 * @param {CartItem} cartItem
 * @return {CartItem}
 */
export const decrementCartItemQuantity = cartItem => {
  const quantity = cartItem.quantity - 1;

  return CartItem(cartItem.productPack, quantity);
};

/**
 * Set cart item quantity.
 *
 * @function
 * @param {number} quantity
 * @param {CartItem} cartItem
 * @return {CartItem}
 */
export const setCartItemQuantity = (quantity, cartItem) => {
  return CartItem(cartItem.productPack, quantity);
};

/**
 * Get cart item quantity.
 *
 * @function
 * @param {CartItem} cartItem
 * @return {number}
 */
export const getCartItemQuantity = cartItem => cartItem.quantity;

/**
 * Get cart item productPack.
 *
 * @function
 * @param {CartItem} cartItem
 * @return {ProductPack}
 */
export const getCartItemProductPack = cartItem => cartItem.productPack;

/**
 * Get product name from cart item.
 *
 * @function
 * @param {CartItem} cartItem
 * @return {string}
 */
export const getCartItemName = cartItem => getProductPackName(cartItem.productPack);

/**
 * Get formula ids from cart item.
 *
 * @function
 * @param {CartItem} cartItem
 * @return {string[]}
 */
export const getCartItemFormulaIds = cartItem => getProductPackFormulaIds(cartItem.productPack);

/**
 * Get product reference from cart item.
 *
 * @function
 * @param {CartItem} cartItem
 * @return {string}
 */
export const getCartItemReference = cartItem => getProductPackReference(cartItem.productPack);

/**
 * Get unique id for cart item.
 *
 * @function
 * @param {CartItem} cartItem
 * @return {string}
 */
export const getCartItemUniqueId = cartItem => getProductPackUniqueId(cartItem.productPack);

export const isCartItemGenericEyeSerum = cartItem =>
  isProductPackGenericEyeSerum(cartItem.productPack);

/**
 * Get product price from cart item (unit price).
 *
 * @function
 * @param {CartItem} cartItem
 * @return {Price}
 */
export const getCartItemPrice = cartItem => getProductPackPrice(cartItem.productPack);

/**
 * Get product pack usage from cart item.
 *
 * @function
 * @param {CartItem} cartItem
 * @return {string}
 */
export const getCartItemUsage = cartItem => getProductPackUsage(cartItem.productPack);

/**
 * Get product size from cart item.
 *
 * @function
 * @param cartItem
 * @return {Size}
 */
export const getCartItemSize = cartItem => getProductPackSize(cartItem.productPack);

/**
 * Check if cart items is serum.
 *
 * @param {CartItem} cartItem
 * @return {boolean}
 */
export const isCartItemSerum = cartItem => isProductPackSerum(cartItem.productPack);
